import { styled } from '@/stitches.config';

import { Box, Label } from '@base-app';
import { Variants } from '@common/types';
import { ReactComponent as Caution } from '@assets/icons/caution.svg';
import { ReactComponent as DropdownIcon } from '@assets/icons/dropdown.svg';
import { ReactComponent as FilterIcon } from '@assets/icons/filter.svg';

export const SelectContainerStyled = styled(Box, {
  position: 'relative',
  variants: {
    variant: {
      [Variants.PRIMARY]: {},
      [Variants.SECONDARY]: {
        border: '1px solid $darkGray700',
        borderRadius: '$pill',
        color: '$darkGray200',
        cursor: 'pointer',
        fontSize: '$md',
        fontWeight: '$medium',
        padding: '$2 $4',
        background: '$coolGray300',
        '&:hover': {},
      },
    },
    disabled: {
      true: {
        '&:hover': {
          cursor: 'not-allowed',
        },
        color: '$coolGray200',
      },
    },
  },
});

export const StyledPlaceholder = styled(Box, {
  color: '$darkGray200',
  variants: {
    variant: {
      [Variants.PRIMARY]: {},
      [Variants.SECONDARY]: {
        color: '$charcoal300',
      },
    },
  },
});

export const SelectInputContainerStyled = styled(Box, {
  textAlign: 'left',
});

export const CautionIconStyled = styled(Caution, {
  color: '$error',
  display: 'inline-block',
  height: '$6',
  width: '$6',
});

export const ErrorDescriptionStyled = styled('p', {
  color: '$error',
  display: 'inline-block',
  fontSize: '$xs',
  fontWeight: '$medium',
  mt: '$0',
  mb: '$0',
});

export const ErrorBoxStyled = styled(Box, {
  alignItems: 'center',
  display: 'flex',
  mt: '$2',
});

export const FlexBoxStyled = styled(Box, {
  alignItems: 'flex-top',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
});

export const SelectButtonStyled = styled('button', {
  // Reset
  border: 'none',
  padding: '$0',
  backgroundColor: 'transparent',

  display: 'flex',
  fontSize: '$md',
  width: '100%',
  height: '$6',
  lineHeight: '$6',
  alignItems: 'flex-end',

  '&:disabled': {
    '&:hover': {
      cursor: 'not-allowed',
    },
    color: '$coolGray200',
  },

  variants: {
    variant: {
      [Variants.PRIMARY]: {
        borderBottom: '$borderWidths$1 solid $darkGray700',
        '&:hover': {
          cursor: 'pointer',
          borderBottom: '$borderWidths$1 solid $charcoal300',
        },
        '&:focus': {
          outline: 'none',
          borderBottom: '$borderWidths$2 solid $darkBlue300',
        },
      },
      [Variants.SECONDARY]: {
        alignItems: 'center',
        color: '$charcoal300',
        fontWeight: '$bold',
        cursor: 'pointer',
      },
    },
    error: {
      true: {
        borderBottom: '$borderWidths$1 solid $error',
      },
    },
  },

  flexDirection: 'row',
  justifyContent: 'space-between',
  position: 'relative',

  '&:not([aria-expanded=true]) + .select-dropdown': {
    transform: 'scaleY(0)',
  },
});

export const IconContainerStyled = styled(Box, {
  display: 'flex',
  color: '$darkGray200',
  variants: {
    disabled: {
      true: {
        color: '$coolGray200',
      },
    },
  },
});

export const FilterIconStyled = styled(FilterIcon, {
  height: '$3',
  marginLeft: '$2',
  color: '$darkBlue300',
  width: '$3',
});

export const DropdownIconStyled = styled(DropdownIcon, {
  height: '$6',
  marginLeft: '$1',
  width: '$6',
});

export const DropdownIconRotatedStyled = styled(DropdownIconStyled, {
  rotate: '180deg',
});

export const SelectDropdownStyled = styled('ul', {
  backgroundColor: '$white',
  borderRadius: '$3',
  boxShadow: '$menu',
  listStyle: 'none',
  listStyleType: 'none',
  maxHeight: '300px',
  overflowX: `auto`,
  padding: '$0',
  position: 'absolute',
  px: '$0',
  py: '$3',
  transformOrigin: 'top center',
  transition: 'transform 0.2s',
  width: '100%',
  zIndex: '$dropdown',

  variants: {
    variant: {
      [Variants.PRIMARY]: {
        marginTop: '$0',
        textAlign: 'left',
      },
      [Variants.SECONDARY]: {
        marginTop: '$3',
      },
      [Variants.TERTIARY]: {
        marginTop: '$0',
        textAlign: 'left',
        maxHeight: '180px',
      },
    },
  },

  'select-button:not([aria-expanded=true]) + &': {
    height: '$0',
    overflow: 'hidden',
    width: '$0',
  },
});

export const SelectListItemStyled = styled('li', {
  variants: {
    variant: {
      [Variants.PRIMARY]: {
        '&[aria-selected=true]': {
          background: '$coolGray700',
          fontWeight: '$medium',
        },
      },
      [Variants.SECONDARY]: {
        color: '$charcoal300',
        fontWeight: '$regular',
        '&[aria-selected=true]': {
          fontWeight: '$bold',
        },
      },
    },
  },
});

export const StyledMultiSelectList = styled('ul', {
  backgroundColor: '$white',
  borderRadius: '$3',
  boxShadow: '$menu',
  color: '$charcoal300',
  display: 'flex',
  flexDirection: 'column',
  fontWeight: '$regular',
  listStyle: 'none',
  listStyleType: 'none',
  marginTop: '$0',
  minWidth: '$dropdownListWidth',
  padding: '$0',
  paddingLeft: '$1',
  position: 'absolute',
  px: '$0',
  py: '$3',
  transformOrigin: 'top center',
  transition: 'transform 0.2s',
  zIndex: '$dropdown',

  '&[aria-selected=true]': {
    fontWeight: '$bold',
  },
});

export const StyledMultiSelectListItem = styled('li', {
  display: 'flex',
});

export const SelectListItemLabelStyled = styled('label', {
  display: 'block',
  width: '100%',
  px: '$3',
  py: '$2',
  '&:hover': {
    cursor: 'pointer',
  },
  variants: {
    multipleSelector: {
      true: {
        textAlign: 'left',
      },
    },
  },
});

export const SelectListItemLabelInputStyled = styled('input', {
  width: '$0',
  height: '$0',
  overflow: 'hidden',
  padding: '$0',
  margin: '$0',
});

export const StyledSecondaryLabel = styled(Label, {
  display: 'inline-flex',
  fontWeight: '300',
  fontSize: '14px',
  marginLeft: '$1',
  textAlign: 'left',
  variants: {
    variant: {
      [Variants.PRIMARY]: {},
      [Variants.SECONDARY]: {
        fontWeight: '$bold',
        fontSize: '$normal',
        lineHeight: '$6',
        color: '$darkBlue400',
        marginRight: '$2',
      },
    },
  },
});
